// @ts-check
// Taylor's Boats — IntersectionObserver wrapper for opt-in scroll reveals.
// Adds .is-visible (also alias .visible) on `[data-tb-reveal]` elements
// when they enter the viewport. Children with [data-tb-stagger] inside a
// revealed parent get a wave delay set via the --tb-delay CSS variable.
//
// Pair with `assets/tb-reveals.css`. Both are loaded globally from the
// theme layout, so any section can add the data attributes and pick up
// the behavior without bundling its own observer.

(function () {
  if (typeof window === 'undefined') return;
  if (window.tbRevealsBooted) return;
  window.tbRevealsBooted = true;

  const STAGGER_MS = 100;

  /** @param {Element} el */
  function reveal(el) {
    el.classList.add('is-visible');
    el.classList.add('visible'); // legacy alias for prototypes that read .visible
    const staggered = el.querySelectorAll('[data-tb-stagger]');
    staggered.forEach((child, idx) => {
      /** @type {HTMLElement} */ (child).style.setProperty('--tb-delay', `${idx * STAGGER_MS}ms`);
      child.classList.add('is-visible');
      child.classList.add('visible');
    });
    // Mask reveals nested inside the revealed scope
    el.querySelectorAll('[data-tb-mask]').forEach((mask) => {
      mask.classList.add('is-visible');
      mask.classList.add('visible');
    });
    // Image wipes nested inside
    el.querySelectorAll('[data-tb-reveal-img]').forEach((img) => {
      img.classList.add('is-visible');
      img.classList.add('visible');
    });
  }

  function init() {
    const targets = document.querySelectorAll('[data-tb-reveal]');
    if (!targets.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      // Reveal everything immediately on unsupported browsers.
      targets.forEach(reveal);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' },
    );

    targets.forEach((el) => observer.observe(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Parallax — moves the .tb-parallax-bg child of each [data-tb-parallax]
  // element up/down based on scroll progress through the viewport. Mild
  // amplitude (60px). Uses requestAnimationFrame to throttle.
  const parallaxEls = document.querySelectorAll('[data-tb-parallax]');
  if (parallaxEls.length === 0) return;

  let ticking = false;
  function updateParallax() {
    parallaxEls.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const winH = window.innerHeight;
      if (rect.bottom < 0 || rect.top > winH) return;
      const progress = (winH - rect.top) / (winH + rect.height);
      const offset = (progress - 0.5) * 60;
      const bg = el.querySelector('.tb-parallax-bg');
      if (bg) {
        /** @type {HTMLElement} */ (bg).style.transform =
          `translate3d(0, ${offset}px, 0) scale(1.1)`;
      }
    });
    ticking = false;
  }

  if (typeof window.matchMedia === 'function') {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced.matches) return;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    },
    { passive: true },
  );
  updateParallax();
})();
